/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";

import {
  Field,
  reduxForm,
  getFormSyncErrors,
  getFormValues,
  change,
} from "redux-form";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  useTheme,
  Box,
  Button,
  InputAdornment,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  Backdrop,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { renderTextField } from "components/FormFields/FieldTypes";
import {
  Required,
  emailValidator,
  phone as phoneValidator,
} from "components/FormFields/validators";
import { authenticationActions as authActions, homeActions } from "actions";
import SecondaryCenterTemplate from "components/SecondaryCenterTemplate";
import { phone as phoneNormalizer } from "components/FormFields/normalizers";
import { phone as phoneFormatter } from "components/FormFields/formatters";
import { Helmet } from "react-helmet";

const SignInView = (props) => {
  const theme = useTheme();
  const {
    onSignIn,
    formErrors,
    formValues,
    signIn,
    loginRefreshtoken,
    authenticateDevice,
    loginStatus,
    closeSignIn,
    resetLoginForgotPasswordStatus,
    updateGTMDataLayer,
    gtmInfo,
    saveSelectedProduct,
    organizationInfo,
    initialize,
  } = props;
  const history = useHistory();
  const locationState = useLocation();
  const matchesMinWidth = useMediaQuery("(max-width:600px)");

  const [showPassword, setShowPassword] = useState(false);
  const [inputType, setInputType] = useState("phone");
  const [loading, setLoading] = useState(false);

  const tagManagerArgs = {
    dataLayer: gtmInfo,
    dataLayerName: "PageDataLayer",
  };

  useEffect(() => {
    document.getElementById("appcontent").scroll(0, 0);
    resetLoginForgotPasswordStatus();
    props.setValue(7);
    updateGTMDataLayer({
      page: "Global",
      screen: "Sign In",
      screen_title: "Global",
      event: "Nav_sign_in",
    });

    const ssoData = locationState?.state;

    if (
      ssoData?.type &&
      (ssoData?.type === "email" || ssoData?.type === "phone") &&
      ssoData?.username &&
      ssoData?.alreadySignedUp
    ) {
      setInputType(ssoData?.type);
      let fillObj = {};
      fillObj[ssoData?.type] = ssoData?.username;
      initialize(fillObj);
    }

    return () => {
      updateGTMDataLayer({
        screen_referrer: "Sign In",
        page: "Global",
        screen: "Sign In",
        screen_title: "Global",
        event: "Nav_sign_in",
      });
    };
  }, []);

  useEffect(() => {
    window.dataLayer.push({
      ...tagManagerArgs.dataLayer,
    });
  }, [gtmInfo]);

  useEffect(() => {
    if (loginRefreshtoken && loginRefreshtoken !== "") {
      authenticateDevice(loginRefreshtoken);
    }
  }, [loginRefreshtoken, authenticateDevice, onSignIn]);

  useEffect(() => {
    if (loginStatus === false || loginStatus === true) {
      setLoading(false);
      if (loginStatus) {
        saveSelectedProduct("");
        history.push("/");
      }
    }
  }, [loginStatus]);

  const onFormSubmit = () => {
    setLoading(true);
    const params = {
      refreshToken: JSON.parse(sessionStorage.getItem("refreshToken")),
      password: formValues.password,
    };
    if (inputType === "phone") {
      params.phone = formValues.phone;
    } else {
      params.email = formValues.email;
    }
    signIn(params);
  };

  return (
    <>
      {loading ? (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress
            style={{ color: theme?.palette?.secondary?.main }}
          />
        </Backdrop>
      ) : null}
      <SecondaryCenterTemplate pageTitle="Sign In">
        <Helmet>
          <title>
            Sign In |{" "}
            {organizationInfo?.app?.appCode === "CVD"
              ? "Cuverd® Partner"
              : "Cuverd®"}
          </title>
          <meta
            name="description"
            content="Sign In now to find savings and discount coupons on your prescription medications with Cuverd®."
          />
          <meta property="og:title" content="Sign Up | Cuverd®" />
          <meta
            property="og:description"
            content="Sign In now to find savings and discount coupons on your prescription medications with Cuverd®."
          />
          <meta
            property="og:image"
            content="assets/images/cuverd-logo-header.svg"
          />
          <link
            rel="alternate"
            href="https://www.cuverd.com/SignIn"
            hrefLang="en-us"
          />
          <link rel="canonical" href="https://www.cuverd.com/SignIn" />
        </Helmet>
        <Grid container alignItems="center" justifyContent="center">
          <Grid item sm={8} sx={{ margin: "auto" }}>
            <form>
              <Box component="div" mb={7} mt={2} sx={{ textAlign: "center" }}>
                <Typography variant="h2LatoBold" sx={{ lineHeight: "26px" }}>
                  Enter the mobile phone number or email address{" "}
                  {matchesMinWidth ? null : <br />} associated with your
                  account:
                </Typography>
              </Box>
              <Box>
                <Box>
                  {inputType === "phone" ? (
                    <Field
                      name="phone"
                      isRequired
                      component={renderTextField}
                      label="Please enter your Mobile phone number"
                      format={phoneFormatter}
                      validate={[Required, phoneValidator]}
                      normalize={phoneNormalizer}
                      inErrorState={false}
                      handleKeyDown={(e) => {
                        if (
                          e.key &&
                          e.key == "Enter" &&
                          Object.keys(formErrors).length == 0
                        ) {
                          onFormSubmit();
                        }
                      }}
                    />
                  ) : (
                    <Field
                      name="email"
                      isRequired
                      component={renderTextField}
                      label="Email Address"
                      validate={[Required, emailValidator]}
                      handleKeyDown={(e) => {
                        if (
                          e.key &&
                          e.key == "Enter" &&
                          Object.keys(formErrors).length == 0
                        ) {
                          onFormSubmit();
                        }
                      }}
                    />
                  )}
                </Box>
                <Box mb={3} mt={4}>
                  <Divider
                    sx={{
                      "&:before": {
                        borderColor: theme.palette.primary.main,
                      },
                      "&:after": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Typography variant="h2LatoBold">OR</Typography>
                  </Divider>
                </Box>
                <Box sx={{ marginBottom: "32px" }}>
                  <Button
                    variant="largeOutline"
                    type="button"
                    onClick={() => {
                      if (inputType === "phone") {
                        setInputType("email");
                        change("phone", "");
                      } else {
                        setInputType("phone");
                        change("email", "");
                      }
                    }}
                  >
                    {inputType === "phone"
                      ? "USE MY EMAIL ADDRESS"
                      : "USE MY PHONE NUMBER"}
                  </Button>
                </Box>
                <Box my={3}>
                  <Field
                    name="password"
                    isRequired
                    component={renderTextField}
                    label="Please enter your password"
                    type={showPassword ? "text" : "password"}
                    validate={Required}
                    handleKeyDown={(e) => {
                      if (
                        e.key &&
                        e.key == "Enter" &&
                        Object.keys(formErrors).length == 0
                      ) {
                        onFormSubmit();
                      }
                    }}
                    endIcon={
                      <InputAdornment
                        position="end"
                        onClick={() => {
                          setShowPassword(!showPassword);
                        }}
                      >
                        {showPassword ? (
                          <Visibility
                            sx={{ color: theme.palette.primary.main }}
                          />
                        ) : (
                          <VisibilityOff
                            sx={{ color: theme.palette.primary.main }}
                          />
                        )}
                      </InputAdornment>
                    }
                  />
                </Box>
              </Box>
              <Box mb={10} mt={1} sx={{ textAlign: "left" }}>
                <Typography
                  variant="h3LatoRegular"
                  onClick={() => {
                    history.push("/ForgotPassword");
                    closeSignIn && closeSignIn();
                  }}
                  sx={{
                    color: theme.palette.common.greenThree,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  I forgot my password
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4LatoRegular">
                  By continuing to use this website, you agree to the Cuverd®{" "}
                  <br />
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="h3LatoRegular"
                  sx={{ color: theme.palette.common.greenFour }}
                  component={Link}
                  to="/terms-and-conditions"
                >
                  Terms and Conditions
                </Typography>
                <Typography variant="h4LatoRegular">&nbsp;and&nbsp;</Typography>
                <Typography
                  variant="h3LatoRegular"
                  sx={{ color: theme.palette.common.greenFour }}
                  component={Link}
                  to="/privacy-policy"
                >
                  Privacy Policy
                </Typography>
              </Box>
              <Box
                component="div"
                mt={6}
                mb={8}
                mx="auto"
                sx={{ width: "90%" }}
              >
                <Button
                  variant="filled"
                  sx={{ width: "100%", fontSize: 18, fontWeight: 600 }}
                  onClick={() => onFormSubmit()}
                  disabled={Object.keys(formErrors).length ? true : false}
                >
                  SIGN IN
                </Button>
              </Box>
            </form>
          </Grid>
        </Grid>
      </SecondaryCenterTemplate>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    formErrors: getFormSyncErrors("SIGNIN_FORM")(state) || {},
    formValues: getFormValues("SIGNIN_FORM")(state),
    signInInfo: state.signIn.data,
    loginMessage: state.signIn.loginMessage,
    loginRefreshtoken: state.signIn.loginRefreshtoken,
    loginStatus: state.signIn.loginStatus,
    gtmInfo: state.gtmInfo.data,
    loginErrorMessage: state.signIn.loginErrorMessage,
    organizationInfo: state.organizationInfo.data,
  };
};

const mapDispatchToProps = {
  authenticateDevice: (value) => authActions.authenticateDevice(value),
  signIn: (params) => homeActions.signIn(params),
  resetLoginStatus: () => homeActions.resetLoginStatus(),
  saveSelectedProduct: (value) => homeActions.saveSelectedProduct(value),
  resetLoginForgotPasswordStatus: () =>
    homeActions.resetLoginForgotPasswordStatus(),
  updateGTMDataLayer: (param) => homeActions.updateGTMDataLayer(param),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "SIGNIN_FORM",
  })(SignInView)
);
